import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Save, RotateCcw, Info, Lock, User, BarChart3, Clock, HelpCircle, Zap, Calendar, Mail, Hash, CheckCircle2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUserRole } from "@/hooks/use-user-role";
import { useNavigation } from "@/components/layout/app-layout";
import { ValuationMethodWarningDialog } from "@/components/valuation-method-warning-dialog";
import { DemoRoleSwitcher } from "@/components/demo-role-switcher";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface StockOptions {
  // Valuation & Costing
  valuationMethod: "average" | "fifo" | "standard";
  allowPerItemOverride: boolean;
  includeLandedCosts: boolean;
  landedCostComponents: {
    freight: boolean;
    duties: boolean;
    insurance: boolean;
  };
  landedCostAllocation: "value" | "weight" | "unit";
  cogsMethod: "match" | "custom";
  costRoundingDecimals: number;
  priceRoundingDecimals: number;

  // Reorder & Replenishment
  reorderPointMethod: "fixed" | "leadTimeDemand" | "percentageMax";
  safetyStockBuffer: number;
  defaultLeadTimeDays: number;
  reorderQuantityMethod: "fixed" | "upToMax" | "eoq" | "periodicReview";
  minimumOrderQuantity: number;
  orderQuantityRounding: number;
  autoGeneratePO: boolean;
  generateSuggestionsOnly: boolean;
  reviewFrequency: "daily" | "weekly" | "monthly";

  // Stock Alerts
  enableLowStockAlerts: boolean;
  lowStockAlertTrigger: "reorderPoint" | "minQuantity";
  notificationDashboard: boolean;
  notificationEmail: boolean;
  notificationSMS: boolean;
  alertFrequency: "realtime" | "daily" | "weekly";
  enableOverstockAlerts: boolean;
  enableSlowMovingAlerts: boolean;
  slowMovingThresholdDays: number;
  negativeStockHandling: "prevent" | "allowWithWarning" | "allowNoWarning";
  alertZeroStock: boolean;
  alertNoSupplier: boolean;
  alertNoReorderPoint: boolean;

  // Item Defaults
  defaultCategory: string;
  defaultLocation: string;
  defaultStatus: "active" | "inactive";
  defaultABCClassification: "A" | "B" | "C";
  defaultWeightUnit: "kg" | "g" | "lbs";
  requireBarcode: boolean;
  autoGenerateBarcodes: boolean;
  barcodeFormat: "ean13" | "upc" | "code128";
  requireUniqueStockCodes: boolean;
  stockCodeFormat: "freeText" | "categoryPrefix" | "autoIncrement";
  defaultMarkupMethod: "percentage" | "fixed";
  defaultMarkupPercentage: number;
  minimumMarginPercentage: number;
  priceRounding: "none" | "nearest99" | "nearest95" | "wholeNumber";
  defaultMinQuantity: number;
  defaultMaxQuantity: number;
  defaultReorderPoint: number;
  defaultReorderQuantity: number;

  // Movement & Transaction Rules
  requireReasonCode: boolean;
  requireAdjustmentApproval: boolean;
  adjustmentApprovalThreshold: number;
  autoPostMovements: boolean;
  requireManualPosting: boolean;
  allowInterWarehouseTransfers: boolean;
  requireTransferApproval: boolean;
  transferApprovalThreshold: number;
  transitHandling: "singleStep" | "twoStep";
  requireWriteOffReason: boolean;
  requireWriteOffApproval: boolean;
  writeOffApprovalThreshold: number;
  stockTakeCountMethod: "blind" | "guided";
  varianceTolerance: number;
  autoAdjustWithinTolerance: boolean;
  requireVarianceApproval: boolean;
  cycleCountFrequencyA: "weekly" | "monthly" | "quarterly" | "annually";
  cycleCountFrequencyB: "weekly" | "monthly" | "quarterly" | "annually";
  cycleCountFrequencyC: "weekly" | "monthly" | "quarterly" | "annually";
  fiscalYearStartMonth: number;
  fiscalYearStartDay: number;
  enableAutoCycleCounting: boolean;

  // Display & Formatting
  quantityDecimals: number;
  priceDecimals: number;
  percentageDecimals: number;
  thousandSeparator: "comma" | "space" | "none";
  decimalSeparator: "period" | "comma";
  dateFormat: "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";
  timeFormat: "12hour" | "24hour";
  timeZone: string;
  defaultView: "table" | "grid" | "cards";
  defaultRowsPerPage: number;
  defaultSort: string;
  defaultSortDirection: "asc" | "desc";
}

const DEFAULT_OPTIONS: StockOptions = {
  // Valuation & Costing
  valuationMethod: "average",
  allowPerItemOverride: false,
  includeLandedCosts: false,
  landedCostComponents: {
    freight: true,
    duties: true,
    insurance: false,
  },
  landedCostAllocation: "value",
  cogsMethod: "match",
  costRoundingDecimals: 2,
  priceRoundingDecimals: 2,

  // Reorder & Replenishment
  reorderPointMethod: "leadTimeDemand",
  safetyStockBuffer: 20,
  defaultLeadTimeDays: 7,
  reorderQuantityMethod: "upToMax",
  minimumOrderQuantity: 1,
  orderQuantityRounding: 1,
  autoGeneratePO: false,
  generateSuggestionsOnly: true,
  reviewFrequency: "daily",

  // Stock Alerts
  enableLowStockAlerts: true,
  lowStockAlertTrigger: "reorderPoint",
  notificationDashboard: true,
  notificationEmail: true,
  notificationSMS: false,
  alertFrequency: "daily",
  enableOverstockAlerts: true,
  enableSlowMovingAlerts: true,
  slowMovingThresholdDays: 90,
  negativeStockHandling: "prevent",
  alertZeroStock: true,
  alertNoSupplier: true,
  alertNoReorderPoint: true,

  // Item Defaults
  defaultCategory: "none",
  defaultLocation: "none",
  defaultStatus: "active",
  defaultABCClassification: "C",
  defaultWeightUnit: "kg",
  requireBarcode: false,
  autoGenerateBarcodes: false,
  barcodeFormat: "ean13",
  requireUniqueStockCodes: true,
  stockCodeFormat: "freeText",
  defaultMarkupMethod: "percentage",
  defaultMarkupPercentage: 30,
  minimumMarginPercentage: 15,
  priceRounding: "nearest99",
  defaultMinQuantity: 0,
  defaultMaxQuantity: 100,
  defaultReorderPoint: 20,
  defaultReorderQuantity: 50,

  // Movement & Transaction Rules
  requireReasonCode: true,
  requireAdjustmentApproval: true,
  adjustmentApprovalThreshold: 1000,
  autoPostMovements: true,
  requireManualPosting: false,
  allowInterWarehouseTransfers: true,
  requireTransferApproval: false,
  transferApprovalThreshold: 5000,
  transitHandling: "twoStep",
  requireWriteOffReason: true,
  requireWriteOffApproval: true,
  writeOffApprovalThreshold: 500,
  stockTakeCountMethod: "blind",
  varianceTolerance: 2,
  autoAdjustWithinTolerance: true,
  requireVarianceApproval: true,
  cycleCountFrequencyA: "monthly",
  cycleCountFrequencyB: "quarterly",
  cycleCountFrequencyC: "annually",
  fiscalYearStartMonth: 7, // July
  fiscalYearStartDay: 1,
  enableAutoCycleCounting: true,

  // Display & Formatting
  quantityDecimals: 0,
  priceDecimals: 2,
  percentageDecimals: 2,
  thousandSeparator: "comma",
  decimalSeparator: "period",
  dateFormat: "DD/MM/YYYY",
  timeFormat: "24hour",
  timeZone: "UTC+00:00",
  defaultView: "table",
  defaultRowsPerPage: 25,
  defaultSort: "code",
  defaultSortDirection: "asc",
};

export function StockOptionsPage() {
  const [options, setOptions] = useState<StockOptions>(DEFAULT_OPTIONS);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("valuation");
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [pendingValuationMethod, setPendingValuationMethod] = useState<"average" | "fifo" | "standard" | null>(null);
  const [stockTakeHelpDialog, setStockTakeHelpDialog] = useState(false);
  const { isAdmin } = useUserRole();
  const { navigateTo } = useNavigation();
  const { flags: featureFlags, updateFlag: updateFeatureFlag, resetToDefaults: resetFeatureFlags, enableAll: enableAllFeatures, disableAll: disableAllFeatures } = useFeatureFlags();

  // Load saved options from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("stockOptions");
    if (saved) {
      try {
        setOptions(JSON.parse(saved));
      } catch (error) {
        console.error("Failed to load stock options:", error);
      }
    }
  }, []);

  // Helper functions for fiscal year calculations
  const getEndOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const getFiscalQuarterEnd = (date: Date, fiscalYearStartMonth: number): Date => {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    
    // Calculate which fiscal quarter we're in
    let fiscalMonth = month - fiscalYearStartMonth + 1;
    if (fiscalMonth <= 0) fiscalMonth += 12;
    
    // Find the end of the current fiscal quarter
    const quarterEndMonth = Math.ceil(fiscalMonth / 3) * 3;
    let endMonth = fiscalYearStartMonth + quarterEndMonth - 1;
    let endYear = year;
    
    if (endMonth > 12) {
      endMonth -= 12;
      endYear += 1;
    }
    
    return new Date(endYear, endMonth, 0); // Last day of the month
  };

  const getFiscalYearEnd = (date: Date, fiscalYearStartMonth: number, fiscalYearStartDay: number): Date => {
    const currentMonth = date.getMonth() + 1;
    const currentYear = date.getFullYear();
    
    // If we're past the fiscal year start, the fiscal year ends next calendar year
    let fiscalYearEndYear = currentYear;
    if (currentMonth >= fiscalYearStartMonth) {
      fiscalYearEndYear = currentYear + 1;
    }
    
    // Fiscal year ends the day before it starts
    const endMonth = fiscalYearStartMonth - 1;
    const endDay = fiscalYearStartDay - 1;
    
    if (endMonth === 0) {
      // December of previous year
      return new Date(fiscalYearEndYear - 1, 11, 31);
    }
    
    return new Date(fiscalYearEndYear, endMonth, endDay === 0 ? new Date(fiscalYearEndYear, endMonth, 0).getDate() : endDay);
  };

  const getFiscalYearDisplay = (fiscalYearStartMonth: number, fiscalYearStartDay: number): string => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    let startYear = currentYear;
    let endYear = currentYear + 1;
    
    if (currentMonth < fiscalYearStartMonth) {
      startYear = currentYear - 1;
      endYear = currentYear;
    }
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const startMonthName = monthNames[fiscalYearStartMonth - 1];
    
    const endMonth = fiscalYearStartMonth - 1 === 0 ? 12 : fiscalYearStartMonth - 1;
    const endDay = fiscalYearStartDay - 1 === 0 ? new Date(endYear, endMonth, 0).getDate() : fiscalYearStartDay - 1;
    const endMonthName = monthNames[endMonth - 1];
    
    return `FY ${startYear}-${endYear} (${startMonthName} ${fiscalYearStartDay}, ${startYear} - ${endMonthName} ${endDay}, ${endYear})`;
  };

  const calculateNextScheduledDate = (frequency: string, fiscalYearStartMonth: number): string => {
    const today = new Date();
    let nextDate: Date;
    
    switch (frequency) {
      case "weekly":
        // End of current week (Sunday)
        const dayOfWeek = today.getDay();
        const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
        nextDate = new Date(today);
        nextDate.setDate(today.getDate() + daysUntilSunday);
        break;
        
      case "monthly":
        // End of current month
        nextDate = getEndOfMonth(today);
        break;
        
      case "quarterly":
        // End of current fiscal quarter
        nextDate = getFiscalQuarterEnd(today, fiscalYearStartMonth);
        break;
        
      case "annually":
        // End of current fiscal year
        nextDate = getFiscalYearEnd(today, fiscalYearStartMonth, 1);
        break;
        
      default:
        nextDate = today;
    }
    
    return nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleChange = (field: keyof StockOptions, value: any) => {
    setOptions((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleValuationMethodChange = (value: "average" | "fifo" | "standard") => {
    // Check if user is admin
    if (!isAdmin) {
      toast.error("Access Denied", {
        description: "Only administrators can change the valuation method.",
      });
      return;
    }

    // If changing from current method, show warning
    if (value !== options.valuationMethod) {
      setPendingValuationMethod(value);
      setIsWarningDialogOpen(true);
    }
  };

  const confirmValuationMethodChange = () => {
    if (pendingValuationMethod) {
      setOptions((prev) => ({ ...prev, valuationMethod: pendingValuationMethod }));
      setHasChanges(true);
      toast.success("Valuation method changed", {
        description: `Changed to ${pendingValuationMethod === "average" ? "Average Cost" : pendingValuationMethod === "fifo" ? "FIFO" : "Standard Cost"}`,
      });
    }
    setIsWarningDialogOpen(false);
    setPendingValuationMethod(null);
  };

  const handleNestedChange = (parent: keyof StockOptions, field: string, value: any) => {
    setOptions((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent] as any),
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    localStorage.setItem("stockOptions", JSON.stringify(options));
    setHasChanges(false);
    toast.success("Stock options saved successfully");
  };

  const handleReset = () => {
    setOptions(DEFAULT_OPTIONS);
    localStorage.removeItem("stockOptions");
    setHasChanges(false);
    toast.info("Stock options reset to defaults");
  };

  const FutureImplementationBadge = () => (
    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
      Future Implementation
    </Badge>
  );

  const InfoTooltip = ({ content }: { content: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Stock Options</h1>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              🟡 Demo Mode
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <User className="h-3 w-3 mr-1" />
                Admin Access
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigateTo("feature-flags")}
            >
              <Zap className="h-4 w-4 mr-2" />
              Feature Flags
            </Button>
            <DemoRoleSwitcher />
          </div>
        </div>
        <p className="text-muted-foreground mt-2">
          Configure stock management settings, valuation methods, and operational parameters.
        </p>
        {!isAdmin && (
          <div className="mt-3 p-3 rounded-lg border border-blue-200 bg-blue-50">
            <div className="flex items-start gap-2 text-sm text-blue-800">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Note:</strong> Valuation method changes require administrator privileges. Contact your system administrator to modify this critical accounting setting.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Configuration Settings</CardTitle>
              <CardDescription>
                Customise how Stock Evo manages inventory, calculates costs, and handles stock movements.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset} disabled={!hasChanges}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="valuation">Valuation</TabsTrigger>
              <TabsTrigger value="reorder">Reorder</TabsTrigger>
              <TabsTrigger value="alerts">Alerts</TabsTrigger>
              <TabsTrigger value="defaults">Defaults</TabsTrigger>
              <TabsTrigger value="movement">Movement</TabsTrigger>
              <TabsTrigger value="display">Display</TabsTrigger>
              <TabsTrigger value="features">
                <Zap className="h-4 w-4 mr-1" />
                Features
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Valuation & Costing */}
            <TabsContent value="valuation" className="space-y-6 mt-6">
              {/* Inventory Valuation Method */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Inventory Valuation Method</h3>
                    <p className="text-sm text-muted-foreground">
                      Choose how stock costs are calculated. This affects financial reporting and cost of goods sold.
                    </p>
                  </div>
                  {!isAdmin && (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <Lock className="h-3 w-3 mr-1" />
                      Admin Only
                    </Badge>
                  )}
                </div>
                <RadioGroup
                  value={options.valuationMethod}
                  onValueChange={handleValuationMethodChange}
                  disabled={!isAdmin}
                >
                  <div className={`flex items-center space-x-2 p-3 border rounded-lg bg-green-50/50 border-green-200 ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <RadioGroupItem value="average" id="average" disabled={!isAdmin} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="average" className="font-medium cursor-pointer">
                          Average Cost (Weighted Average)
                        </Label>
                        <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                          ✓ Implemented
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Simple, widely used, reduces price volatility. Recommended for most businesses.
                      </p>
                    </div>
                    <Badge variant="secondary">Recommended</Badge>
                  </div>
                  <div className={`flex items-center space-x-2 p-3 border rounded-lg bg-blue-50/50 border-blue-200 ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <RadioGroupItem value="fifo" id="fifo" disabled={!isAdmin} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="fifo" className="font-medium cursor-pointer">
                          FIFO (First In, First Out)
                        </Label>
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-300">
                          ✓ Implemented
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Best for perishables, fashion, electronics. Matches physical flow of goods.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                    <RadioGroupItem value="standard" id="standard" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="standard" className="font-medium cursor-pointer">
                          Standard Cost
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Best for manufacturing with consistent production. Uses predetermined costs.
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="allowPerItemOverride"
                    checked={options.allowPerItemOverride}
                    onCheckedChange={(checked) => handleChange("allowPerItemOverride", checked)}
                    disabled
                  />
                  <Label htmlFor="allowPerItemOverride" className="flex items-center gap-2">
                    Allow per-item valuation method override
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Coming Soon
                    </Badge>
                  </Label>
                </div>
              </div>

              <Separator />

              {/* Landed Cost Settings */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Landed Cost Settings</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure how additional costs (freight, duties, insurance) are included in stock valuation.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="includeLandedCosts"
                    checked={options.includeLandedCosts}
                    onCheckedChange={(checked) => handleChange("includeLandedCosts", checked)}
                    disabled
                  />
                  <Label htmlFor="includeLandedCosts" className="flex items-center gap-2">
                    Include Landed Costs in Item Valuation
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Coming Soon
                    </Badge>
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  Add freight, duties, and other costs to stock value for more accurate costing
                </p>
              </div>

                {options.includeLandedCosts && (
                  <div className="ml-6 space-y-3 p-4 border rounded-lg bg-muted/50">
                    <div className="font-medium text-sm">Cost Components to Include:</div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="freight"
                          checked={options.landedCostComponents.freight}
                          onCheckedChange={(checked) =>
                            handleNestedChange("landedCostComponents", "freight", checked)
                          }
                        />
                        <Label htmlFor="freight">Freight/Shipping Costs</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="duties"
                          checked={options.landedCostComponents.duties}
                          onCheckedChange={(checked) =>
                            handleNestedChange("landedCostComponents", "duties", checked)
                          }
                        />
                        <Label htmlFor="duties">Import Duties & Customs Fees</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="insurance"
                          checked={options.landedCostComponents.insurance}
                          onCheckedChange={(checked) =>
                            handleNestedChange("landedCostComponents", "insurance", checked)
                          }
                        />
                        <Label htmlFor="insurance">Insurance Costs</Label>
                      </div>
                    </div>

                    <div className="space-y-2 mt-4">
                      <Label>Allocation Method</Label>
                      <RadioGroup
                        value={options.landedCostAllocation}
                        onValueChange={(value) => handleChange("landedCostAllocation", value)}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="value" id="value" />
                          <Label htmlFor="value" className="cursor-pointer">By Value</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="weight" id="weight" />
                          <Label htmlFor="weight" className="cursor-pointer">By Weight</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="unit" id="unit" />
                          <Label htmlFor="unit" className="cursor-pointer">By Unit</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Cost Calculation */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Cost Calculation</h3>
                  <p className="text-sm text-muted-foreground">
                    Control how costs are rounded and displayed in the system.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>COGS Method</Label>
                    <p className="text-xs text-muted-foreground">
                      How cost of goods sold is calculated
                    </p>
                    <Select
                      value={options.cogsMethod}
                      onValueChange={(value) => handleChange("cogsMethod", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="match">Match Valuation Method (Recommended)</SelectItem>
                        <SelectItem value="custom">Use Different Method</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cost Rounding (Decimal Places)</Label>
                    <p className="text-xs text-muted-foreground">
                      Number of decimal places for cost calculations
                    </p>
                    <Select
                      value={options.costRoundingDecimals.toString()}
                      onValueChange={(value) => handleChange("costRoundingDecimals", parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (Whole numbers)</SelectItem>
                        <SelectItem value="2">2 (e.g., 10.25)</SelectItem>
                        <SelectItem value="4">4 (e.g., 10.2500)</SelectItem>
                        <SelectItem value="6">6 (e.g., 10.250000)</SelectItem>
                        <SelectItem value="8">8 (e.g., 10.25000000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Price Rounding (Decimal Places)</Label>
                    <Select
                      value={options.priceRoundingDecimals.toString()}
                      onValueChange={(value) => handleChange("priceRoundingDecimals", parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (Whole numbers)</SelectItem>
                        <SelectItem value="2">2 (e.g., 19.99)</SelectItem>
                        <SelectItem value="4">4 (e.g., 19.9900)</SelectItem>
                        <SelectItem value="6">6 (e.g., 19.990000)</SelectItem>
                        <SelectItem value="8">8 (e.g., 19.99000000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Reorder & Replenishment */}
            <TabsContent value="reorder" className="space-y-6 mt-6">
              {/* Default Values for New Items */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Default Reorder Values for New Items</h3>
                  <p className="text-sm text-muted-foreground">
                    Set default inventory levels for new stock items. These can be adjusted per item later.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Min Quantity</Label>
                    <Input
                      type="number"
                      value={options.defaultMinQuantity}
                      onChange={(e) => handleChange("defaultMinQuantity", parseInt(e.target.value))}
                      min="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum stock level before triggering alerts
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Max Quantity</Label>
                    <Input
                      type="number"
                      value={options.defaultMaxQuantity}
                      onChange={(e) => handleChange("defaultMaxQuantity", parseInt(e.target.value))}
                      min="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum stock level to maintain
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Reorder Point</Label>
                    <Input
                      type="number"
                      value={options.defaultReorderPoint}
                      onChange={(e) => handleChange("defaultReorderPoint", parseInt(e.target.value))}
                      min="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Stock level that triggers reorder alerts
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Reorder Quantity</Label>
                    <Input
                      type="number"
                      value={options.defaultReorderQuantity}
                      onChange={(e) => handleChange("defaultReorderQuantity", parseInt(e.target.value))}
                      min="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Suggested quantity to order when restocking
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Basic Alert Settings */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Reorder Alert Settings</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure when and how the system alerts you about low stock situations.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enableLowStockAlerts"
                      checked={options.enableLowStockAlerts}
                      onCheckedChange={(checked) => handleChange("enableLowStockAlerts", checked)}
                    />
                    <Label htmlFor="enableLowStockAlerts">Enable low stock alerts</Label>
                  </div>

                  {options.enableLowStockAlerts && (
                    <div className="ml-6 space-y-3 p-4 border rounded-lg bg-muted/50">
                      <div className="space-y-2">
                        <Label>Trigger alerts when stock reaches</Label>
                        <RadioGroup
                          value={options.lowStockAlertTrigger}
                          onValueChange={(value) => handleChange("lowStockAlertTrigger", value)}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="reorderPoint" id="reorderPoint" />
                            <Label htmlFor="reorderPoint" className="cursor-pointer">Reorder Point (Recommended)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="minQuantity" id="minQuantity" />
                            <Label htmlFor="minQuantity" className="cursor-pointer">Minimum Quantity</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="alertZeroStock"
                      checked={options.alertZeroStock}
                      onCheckedChange={(checked) => handleChange("alertZeroStock", checked)}
                    />
                    <Label htmlFor="alertZeroStock">Alert when stock reaches zero</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="alertNoReorderPoint"
                      checked={options.alertNoReorderPoint}
                      onCheckedChange={(checked) => handleChange("alertNoReorderPoint", checked)}
                    />
                    <Label htmlFor="alertNoReorderPoint">Alert for items with no reorder point set</Label>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Advanced Features - Coming Soon */}
              <div className="space-y-4 opacity-60">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Advanced Reorder Features</h3>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  These features will be available in the AI Forecasting & Analytics module
                </p>

                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  {/* Reorder Point Calculation */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold">Automatic Reorder Point Calculation</h4>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Calculation Method</Label>
                      <Select
                        value={options.reorderPointMethod}
                        onValueChange={(value) => handleChange("reorderPointMethod", value)}
                        disabled
                      >
                        <SelectTrigger className="bg-muted/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Quantity</SelectItem>
                          <SelectItem value="leadTimeDemand">Lead Time Demand (Avg Daily Usage × Lead Time)</SelectItem>
                          <SelectItem value="percentageMax">Percentage of Max Stock</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Safety Stock Buffer (%)</Label>
                        <Input
                          type="number"
                          value={options.safetyStockBuffer}
                          onChange={(e) => handleChange("safetyStockBuffer", parseInt(e.target.value))}
                          min="0"
                          max="100"
                          disabled
                          className="bg-muted/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Default Lead Time (Days)</Label>
                        <Input
                          type="number"
                          value={options.defaultLeadTimeDays}
                          onChange={(e) => handleChange("defaultLeadTimeDays", parseInt(e.target.value))}
                          min="0"
                          disabled
                          className="bg-muted/50"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Reorder Quantity Calculation */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold">Automatic Reorder Quantity Calculation</h4>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Calculation Method</Label>
                      <Select
                        value={options.reorderQuantityMethod}
                        onValueChange={(value) => handleChange("reorderQuantityMethod", value)}
                        disabled
                      >
                        <SelectTrigger className="bg-muted/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Quantity</SelectItem>
                          <SelectItem value="upToMax">Up to Max Stock (Max - Current)</SelectItem>
                          <SelectItem value="eoq">Economic Order Quantity (EOQ)</SelectItem>
                          <SelectItem value="periodicReview">Periodic Review (Days Supply)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Minimum Order Quantity</Label>
                        <Input
                          type="number"
                          value={options.minimumOrderQuantity}
                          onChange={(e) => handleChange("minimumOrderQuantity", parseInt(e.target.value))}
                          min="1"
                          disabled
                          className="bg-muted/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Order Quantity Rounding</Label>
                        <Input
                          type="number"
                          value={options.orderQuantityRounding}
                          onChange={(e) => handleChange("orderQuantityRounding", parseInt(e.target.value))}
                          min="1"
                          disabled
                          className="bg-muted/50"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Automatic Reorder */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold">Automatic Purchase Order Generation</h4>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="autoGeneratePO"
                          checked={options.autoGeneratePO}
                          onCheckedChange={(checked) => handleChange("autoGeneratePO", checked)}
                          disabled
                        />
                        <Label htmlFor="autoGeneratePO" className="text-muted-foreground">
                          Enable automatic purchase order generation
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="generateSuggestionsOnly"
                          checked={options.generateSuggestionsOnly}
                          onCheckedChange={(checked) => handleChange("generateSuggestionsOnly", checked)}
                          disabled
                        />
                        <Label htmlFor="generateSuggestionsOnly" className="text-muted-foreground">
                          Generate reorder suggestions only
                        </Label>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Review Frequency</Label>
                        <Select
                          value={options.reviewFrequency}
                          onValueChange={(value) => handleChange("reviewFrequency", value)}
                          disabled
                        >
                          <SelectTrigger className="bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50">
                  <div className="flex items-start gap-2 text-sm text-blue-800">
                    <BarChart3 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">AI-Powered Reorder Management</p>
                      <p className="text-xs mt-1">
                        The AI Forecasting & Analytics module will provide intelligent reorder point calculations,
                        demand forecasting, economic order quantity optimisation, and automated purchase order generation
                        based on historical data and predictive analytics.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: Stock Alerts & Notifications */}
            <TabsContent value="alerts" className="space-y-6 mt-6">
              {/* Low Stock Alerts */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Low Stock Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure when and how low stock alerts are triggered
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="enableLowStockAlerts"
                    checked={options.enableLowStockAlerts}
                    onCheckedChange={(checked) => handleChange("enableLowStockAlerts", checked)}
                  />
                  <Label htmlFor="enableLowStockAlerts">Enable low stock alerts</Label>
                </div>

                {options.enableLowStockAlerts && (
                  <div className="ml-6 space-y-4 p-4 border rounded-lg bg-muted/50">
                    <div className="space-y-2">
                      <Label>Alert When</Label>
                      <RadioGroup
                        value={options.lowStockAlertTrigger}
                        onValueChange={(value) => handleChange("lowStockAlertTrigger", value)}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="reorderPoint" id="reorderPoint" />
                          <Label htmlFor="reorderPoint" className="cursor-pointer">At Reorder Point</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="minQuantity" id="minQuantity" />
                          <Label htmlFor="minQuantity" className="cursor-pointer">Below Min Quantity</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label>Notification Method</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="notificationDashboard"
                            checked={options.notificationDashboard}
                            onCheckedChange={(checked) => handleChange("notificationDashboard", checked)}
                          />
                          <Label htmlFor="notificationDashboard">Dashboard notification</Label>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Alerts will appear in your dashboard and stock item pages
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Alert Frequency</Label>
                      <Select
                        value={options.alertFrequency}
                        onValueChange={(value) => handleChange("alertFrequency", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="realtime">Real-time</SelectItem>
                          <SelectItem value="daily">Once per day</SelectItem>
                          <SelectItem value="weekly">Once per week</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        How often to check for low stock conditions
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Negative Stock Handling */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Negative Stock Handling</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure how the system handles negative stock situations
                  </p>
                </div>

                <RadioGroup
                  value={options.negativeStockHandling}
                  onValueChange={(value) => handleChange("negativeStockHandling", value)}
                >
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="prevent" id="prevent" />
                    <div className="flex-1">
                      <Label htmlFor="prevent" className="font-medium cursor-pointer">
                        Prevent negative stock (block transactions)
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Recommended for most businesses
                      </p>
                    </div>
                    <Badge variant="secondary">Recommended</Badge>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="allowWithWarning" id="allowWithWarning" />
                    <div className="flex-1">
                      <Label htmlFor="allowWithWarning" className="font-medium cursor-pointer">
                        Allow negative stock (with warning)
                      </Label>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="allowNoWarning" id="allowNoWarning" />
                    <div className="flex-1">
                      <Label htmlFor="allowNoWarning" className="font-medium cursor-pointer">
                        Allow negative stock (no warning)
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              {/* Critical Stock Alerts */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Critical Stock Alerts</h3>
                  <p className="text-sm text-muted-foreground">
                    Alert for critical stock situations
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="alertZeroStock"
                      checked={options.alertZeroStock}
                      onCheckedChange={(checked) => handleChange("alertZeroStock", checked)}
                    />
                    <Label htmlFor="alertZeroStock">Alert when stock reaches zero</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="alertNoReorderPoint"
                      checked={options.alertNoReorderPoint}
                      onCheckedChange={(checked) => handleChange("alertNoReorderPoint", checked)}
                    />
                    <Label htmlFor="alertNoReorderPoint">Alert for items with no reorder point set</Label>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Advanced Alert Features - Coming Soon */}
              <div className="space-y-4 opacity-60">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Advanced Alert Features</h3>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  These features require additional integrations and will be available in future updates
                </p>

                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  {/* External Notification Methods */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">External Notification Methods</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="notificationEmail"
                          checked={options.notificationEmail}
                          onCheckedChange={(checked) => handleChange("notificationEmail", checked)}
                          disabled
                        />
                        <Label htmlFor="notificationEmail" className="text-muted-foreground">
                          Email notification
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="notificationSMS"
                          checked={options.notificationSMS}
                          onCheckedChange={(checked) => handleChange("notificationSMS", checked)}
                          disabled
                        />
                        <Label htmlFor="notificationSMS" className="text-muted-foreground">
                          SMS notification
                        </Label>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Overstock & Slow-Moving Alerts */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Overstock & Slow-Moving Alerts</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="enableOverstockAlerts"
                          checked={options.enableOverstockAlerts}
                          onCheckedChange={(checked) => handleChange("enableOverstockAlerts", checked)}
                          disabled
                        />
                        <Label htmlFor="enableOverstockAlerts" className="text-muted-foreground">
                          Alert when stock exceeds maximum quantity
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="enableSlowMovingAlerts"
                          checked={options.enableSlowMovingAlerts}
                          onCheckedChange={(checked) => handleChange("enableSlowMovingAlerts", checked)}
                          disabled
                        />
                        <Label htmlFor="enableSlowMovingAlerts" className="text-muted-foreground">
                          Alert for slow-moving stock
                        </Label>
                      </div>
                      <div className="ml-6 space-y-2">
                        <Label className="text-muted-foreground">Slow-Moving Threshold (Days)</Label>
                        <Input
                          type="number"
                          value={options.slowMovingThresholdDays}
                          onChange={(e) => handleChange("slowMovingThresholdDays", parseInt(e.target.value))}
                          min="1"
                          disabled
                          className="bg-muted/50"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Supplier Alerts */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Supplier Alerts</h4>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="alertNoSupplier"
                        checked={options.alertNoSupplier}
                        onCheckedChange={(checked) => handleChange("alertNoSupplier", checked)}
                        disabled
                      />
                      <Label htmlFor="alertNoSupplier" className="text-muted-foreground">
                        Alert for items with no supplier assigned
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50">
                  <div className="flex items-start gap-2 text-sm text-blue-800">
                    <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Advanced Notification System</p>
                      <p className="text-xs mt-1">
                        Email and SMS notifications require integration with external services. Overstock and slow-moving 
                        alerts require historical data analysis and will be available in the AI Forecasting & Analytics module.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 4: Item Defaults */}
            <TabsContent value="defaults" className="space-y-6 mt-6">
              {/* Default Values for New Items */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Default Values for New Items</h3>
                  <p className="text-sm text-muted-foreground">
                    Set default values that will be pre-filled when creating new stock items
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Category</Label>
                    <Select
                      value={options.defaultCategory}
                      onValueChange={(value) => handleChange("defaultCategory", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="electronics">Electronics</SelectItem>
                        <SelectItem value="furniture">Furniture</SelectItem>
                        <SelectItem value="office-supplies">Office Supplies</SelectItem>
                        <SelectItem value="raw-materials">Raw Materials</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Location</Label>
                    <Select
                      value={options.defaultLocation}
                      onValueChange={(value) => handleChange("defaultLocation", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="warehouse-a">Warehouse A</SelectItem>
                        <SelectItem value="warehouse-b">Warehouse B</SelectItem>
                        <SelectItem value="warehouse-c">Warehouse C</SelectItem>
                        <SelectItem value="retail-store">Retail Store</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Status</Label>
                    <RadioGroup
                      value={options.defaultStatus}
                      onValueChange={(value) => handleChange("defaultStatus", value)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="active" id="active" />
                        <Label htmlFor="active" className="cursor-pointer">Active</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="inactive" id="inactive" />
                        <Label htmlFor="inactive" className="cursor-pointer">Inactive</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label>Default ABC Classification</Label>
                    <Select
                      value={options.defaultABCClassification}
                      onValueChange={(value) => handleChange("defaultABCClassification", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A (High Value)</SelectItem>
                        <SelectItem value="B">B (Medium Value)</SelectItem>
                        <SelectItem value="C">C (Low Value)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Weight Unit</Label>
                    <Select
                      value={options.defaultWeightUnit}
                      onValueChange={(value) => handleChange("defaultWeightUnit", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kilograms (kg)</SelectItem>
                        <SelectItem value="g">Grams (g)</SelectItem>
                        <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Barcode & Identification */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Barcode & Identification</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure barcode and stock code requirements
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requireBarcode"
                      checked={options.requireBarcode}
                      onCheckedChange={(checked) => handleChange("requireBarcode", checked)}
                    />
                    <Label htmlFor="requireBarcode">Require barcode for new items</Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Barcode Format</Label>
                    <Select
                      value={options.barcodeFormat}
                      onValueChange={(value) => handleChange("barcodeFormat", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ean13">EAN-13</SelectItem>
                        <SelectItem value="upc">UPC</SelectItem>
                        <SelectItem value="code128">Code 128</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requireUniqueStockCodes"
                      checked={options.requireUniqueStockCodes}
                      onCheckedChange={(checked) => handleChange("requireUniqueStockCodes", checked)}
                    />
                    <Label htmlFor="requireUniqueStockCodes">Require unique stock codes</Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Stock Code Format</Label>
                    <RadioGroup
                      value={options.stockCodeFormat}
                      onValueChange={(value) => handleChange("stockCodeFormat", value)}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="freeText" id="freeText" />
                        <Label htmlFor="freeText" className="cursor-pointer">Free text</Label>
                      </div>
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground ml-6">
                      Users can enter any stock code format manually
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Pricing Defaults */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Pricing Defaults</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure default pricing and margin settings
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Markup Method</Label>
                    <Select
                      value={options.defaultMarkupMethod}
                      onValueChange={(value) => handleChange("defaultMarkupMethod", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Markup %</Label>
                    <Input
                      type="number"
                      value={options.defaultMarkupPercentage}
                      onChange={(e) => handleChange("defaultMarkupPercentage", parseInt(e.target.value))}
                      min="0"
                      max="1000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Minimum Margin %</Label>
                    <Input
                      type="number"
                      value={options.minimumMarginPercentage}
                      onChange={(e) => handleChange("minimumMarginPercentage", parseInt(e.target.value))}
                      min="0"
                      max="100"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Price Rounding</Label>
                    <Select
                      value={options.priceRounding}
                      onValueChange={(value) => handleChange("priceRounding", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No rounding</SelectItem>
                        <SelectItem value="nearest99">Nearest .99 (e.g., 19.99)</SelectItem>
                        <SelectItem value="nearest95">Nearest .95 (e.g., 19.95)</SelectItem>
                        <SelectItem value="wholeNumber">Nearest whole number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Advanced Identification Features - Coming Soon */}
              <div className="space-y-4 opacity-60">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Advanced Identification Features</h3>
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Automatic barcode and stock code generation features
                </p>

                <div className="space-y-4">
                  {/* Barcode Auto-Generation */}
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">Barcode Auto-Generation</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="autoGenerateBarcodes-disabled"
                        checked={options.autoGenerateBarcodes}
                        disabled
                      />
                      <Label htmlFor="autoGenerateBarcodes-disabled" className="text-muted-foreground">
                        Auto-generate barcodes for new items
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Automatically generate unique barcodes when creating new stock items
                    </p>
                  </div>

                  <Separator className="opacity-50" />

                  {/* Stock Code Auto-Generation */}
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">Stock Code Auto-Generation</h4>
                    </div>
                    <RadioGroup value={options.stockCodeFormat} disabled>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="categoryPrefix" id="categoryPrefix-disabled" disabled />
                        <Label htmlFor="categoryPrefix-disabled" className="text-muted-foreground">
                          Category prefix + number
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">
                        e.g., ELEC-001, FURN-042
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <RadioGroupItem value="autoIncrement" id="autoIncrement-disabled" disabled />
                        <Label htmlFor="autoIncrement-disabled" className="text-muted-foreground">
                          Auto-increment number only
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">
                        e.g., 00001, 00002, 00003
                      </p>
                    </RadioGroup>
                  </div>
                </div>

                {/* Info Box */}
                <div className="flex gap-3 p-4 border border-blue-200 bg-blue-50/50 rounded-lg">
                  <Hash className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900">
                      Automatic ID Generation
                    </p>
                    <p className="text-sm text-blue-700">
                      Barcode and stock code auto-generation require database sequences, unique ID generation, 
                      and collision handling. These features will be available in a future update.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 5: Movement & Transaction Rules */}
            <TabsContent value="movement" className="space-y-6 mt-6">
              {/* Basic Movement Controls */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Basic Movement Controls</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure basic validation rules for stock movements
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requireReasonCode"
                      checked={options.requireReasonCode}
                      onCheckedChange={(checked) => handleChange("requireReasonCode", checked)}
                    />
                    <Label htmlFor="requireReasonCode">Require reason code for all movements</Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Mandate reason codes when creating stock movements
                  </p>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requireWriteOffReason"
                      checked={options.requireWriteOffReason}
                      onCheckedChange={(checked) => handleChange("requireWriteOffReason", checked)}
                    />
                    <Label htmlFor="requireWriteOffReason">Require reason for write-offs</Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Mandate reason codes when writing off stock
                  </p>
                </div>
              </div>

              <Separator />

              {/* Transfer Configuration */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Transfer Configuration</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure how inter-warehouse transfers work
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="allowInterWarehouseTransfers"
                      checked={options.allowInterWarehouseTransfers}
                      onCheckedChange={(checked) => handleChange("allowInterWarehouseTransfers", checked)}
                    />
                    <Label htmlFor="allowInterWarehouseTransfers">Allow inter-warehouse transfers</Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Enable stock transfers between warehouses
                  </p>

                  <div className="space-y-2 mt-4">
                    <Label>Transit Handling</Label>
                    <p className="text-xs text-muted-foreground">
                      How transfers are processed between warehouses
                    </p>
                    <RadioGroup
                      value={options.transitHandling}
                      onValueChange={(value) => handleChange("transitHandling", value)}
                      className="mt-2"
                    >
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="singleStep" id="singleStep" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="singleStep" className="cursor-pointer font-medium">
                            Single-step (instant transfer)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Stock moves directly from source to destination
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="twoStep" id="twoStep" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="twoStep" className="cursor-pointer font-medium">
                            Two-step (Out from source → In to destination)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Track stock in transit between warehouses
                          </p>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Stock Take - Basic Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Stock Take - Basic Settings</h3>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => setStockTakeHelpDialog(true)}
                    className="h-8 w-8"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Configure basic stock counting methods and variance handling
                </p>

                <div className="space-y-3">
                  {/* General Settings - Apply to All Stock Take Types */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">General Settings</span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      These settings apply to all stock take types: Full Stock Take, Partial Stock Take, Location Audit, and Cycle Count
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Count Method</Label>
                    <p className="text-xs text-muted-foreground">
                      How stock counts are performed
                    </p>
                    <RadioGroup
                      value={options.stockTakeCountMethod}
                      onValueChange={(value) => handleChange("stockTakeCountMethod", value)}
                      className="mt-2"
                    >
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="blind" id="blind" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="blind" className="cursor-pointer font-medium">Blind Count</Label>
                          <p className="text-sm text-muted-foreground">Hide system quantity from counter (more accurate)</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="guided" id="guided" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="guided" className="cursor-pointer font-medium">Guided Count</Label>
                          <p className="text-sm text-muted-foreground">Show system quantity to counter (faster)</p>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label>Variance Tolerance (%)</Label>
                    <Input
                      type="number"
                      value={options.varianceTolerance}
                      onChange={(e) => handleChange("varianceTolerance", parseInt(e.target.value))}
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-muted-foreground">
                      Acceptable variance percentage before requiring approval
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="autoAdjustWithinTolerance"
                      checked={options.autoAdjustWithinTolerance}
                      onCheckedChange={(checked) => handleChange("autoAdjustWithinTolerance", checked)}
                    />
                    <Label htmlFor="autoAdjustWithinTolerance">Auto-adjust within tolerance</Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Automatically adjust stock for variances within tolerance
                  </p>
                </div>
              </div>

              <Separator />

              {/* Fiscal Year Configuration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Fiscal Year Configuration</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Configure your organisation's fiscal year dates
                </p>

                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Fiscal Year Settings</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Fiscal Year Start Month</Label>
                      <Select
                        value={options.fiscalYearStartMonth.toString()}
                        onValueChange={(value) => handleChange("fiscalYearStartMonth", parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">January</SelectItem>
                          <SelectItem value="2">February</SelectItem>
                          <SelectItem value="3">March</SelectItem>
                          <SelectItem value="4">April</SelectItem>
                          <SelectItem value="5">May</SelectItem>
                          <SelectItem value="6">June</SelectItem>
                          <SelectItem value="7">July</SelectItem>
                          <SelectItem value="8">August</SelectItem>
                          <SelectItem value="9">September</SelectItem>
                          <SelectItem value="10">October</SelectItem>
                          <SelectItem value="11">November</SelectItem>
                          <SelectItem value="12">December</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Fiscal Year Start Day</Label>
                      <Select
                        value={options.fiscalYearStartDay.toString()}
                        onValueChange={(value) => handleChange("fiscalYearStartDay", parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <SelectItem key={day} value={day.toString()}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Current Fiscal Year:</strong> {getFiscalYearDisplay(options.fiscalYearStartMonth, options.fiscalYearStartDay)}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Advanced Movement Features - Coming Soon */}
              <div className="space-y-4 opacity-60">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Advanced Movement Features</h3>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Advanced features requiring approval workflow system
                </p>

                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Approval Workflow Required</span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      These features require a complete approval workflow system with user management, notifications, and approval history tracking. They will be available in a future update.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-muted-foreground">Approval Workflows</h4>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="requireAdjustmentApproval"
                        checked={options.requireAdjustmentApproval}
                        onCheckedChange={(checked) => handleChange("requireAdjustmentApproval", checked)}
                        disabled
                      />
                      <Label htmlFor="requireAdjustmentApproval" className="text-muted-foreground">
                        Require approval for adjustments
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Stock adjustments above threshold need approval
                    </p>

                    <div className="ml-6 space-y-2">
                      <Label className="text-muted-foreground">Adjustment Approval Threshold (Value)</Label>
                      <Input
                        type="number"
                        value={options.adjustmentApprovalThreshold}
                        onChange={(e) => handleChange("adjustmentApprovalThreshold", parseInt(e.target.value))}
                        min="0"
                        disabled
                        className="bg-muted/50"
                      />
                      <p className="text-xs text-muted-foreground">
                        Adjustments above this value require approval
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="requireTransferApproval"
                        checked={options.requireTransferApproval}
                        onCheckedChange={(checked) => handleChange("requireTransferApproval", checked)}
                        disabled
                      />
                      <Label htmlFor="requireTransferApproval" className="text-muted-foreground">
                        Require approval for transfers
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Inter-warehouse transfers above threshold need approval
                    </p>

                    <div className="ml-6 space-y-2">
                      <Label className="text-muted-foreground">Transfer Approval Threshold (Value)</Label>
                      <Input
                        type="number"
                        value={options.transferApprovalThreshold}
                        onChange={(e) => handleChange("transferApprovalThreshold", parseInt(e.target.value))}
                        min="0"
                        disabled
                        className="bg-muted/50"
                      />
                      <p className="text-xs text-muted-foreground">
                        Transfers above this value require approval
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="requireWriteOffApproval"
                        checked={options.requireWriteOffApproval}
                        onCheckedChange={(checked) => handleChange("requireWriteOffApproval", checked)}
                        disabled
                      />
                      <Label htmlFor="requireWriteOffApproval" className="text-muted-foreground">
                        Require approval for write-offs
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Write-offs above threshold need approval
                    </p>

                    <div className="ml-6 space-y-2">
                      <Label className="text-muted-foreground">Write-off Approval Threshold (Value)</Label>
                      <Input
                        type="number"
                        value={options.writeOffApprovalThreshold}
                        onChange={(e) => handleChange("writeOffApprovalThreshold", parseInt(e.target.value))}
                        min="0"
                        disabled
                        className="bg-muted/50"
                      />
                      <p className="text-xs text-muted-foreground">
                        Write-offs above this value require approval
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="requireVarianceApproval"
                        checked={options.requireVarianceApproval}
                        onCheckedChange={(checked) => handleChange("requireVarianceApproval", checked)}
                        disabled
                      />
                      <Label htmlFor="requireVarianceApproval" className="text-muted-foreground">
                        Require variance approval
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Variances above tolerance need approval
                    </p>

                    <Separator className="my-4" />

                    <h4 className="text-sm font-semibold text-muted-foreground">Posting Options</h4>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="autoPostMovements"
                        checked={options.autoPostMovements}
                        onCheckedChange={(checked) => handleChange("autoPostMovements", checked)}
                        disabled
                      />
                      <Label htmlFor="autoPostMovements" className="text-muted-foreground">
                        Auto-post movements immediately
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      Automatically post movements without manual review
                    </p>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="requireManualPosting"
                        checked={options.requireManualPosting}
                        onCheckedChange={(checked) => handleChange("requireManualPosting", checked)}
                        disabled
                      />
                      <Label htmlFor="requireManualPosting" className="text-muted-foreground">
                        Require manual posting approval
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      All movements need manual approval before posting
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* ABC-Based Cycle Counting - Coming Soon */}
              <div className="space-y-4 opacity-60">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">ABC-Based Cycle Counting</h3>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Automatic cycle counting based on ABC classification
                </p>

                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">AI Forecasting & Analytics Required</span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      This feature requires ABC Classification from the AI Forecasting & Analytics module. Items are automatically classified and scheduled for counting based on their value and importance.
                    </p>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-purple-900 dark:text-purple-100">ABC-Based Cycle Count Settings</span>
                    </div>
                    <p className="text-xs text-purple-700 dark:text-purple-300">
                      Items are automatically selected based on ABC classification. Configure ABC in AI Forecasting module.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enableAutoCycleCounting"
                      checked={options.enableAutoCycleCounting}
                      onCheckedChange={(checked) => handleChange("enableAutoCycleCounting", checked)}
                      disabled
                    />
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="enableAutoCycleCounting" className="text-muted-foreground">
                        Enable Automatic Cycle Counting
                      </Label>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Automatically create recurring cycle count schedules
                  </p>

                  <div className="space-y-2 mt-4">
                    <Label className="text-muted-foreground">Cycle Count Frequency (by ABC Class)</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">A-Class Items</Label>
                        <Select
                          value={options.cycleCountFrequencyA}
                          onValueChange={(value) => handleChange("cycleCountFrequencyA", value)}
                          disabled
                        >
                          <SelectTrigger className="bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annually">Annually</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">High-value items (80% of value)</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">B-Class Items</Label>
                        <Select
                          value={options.cycleCountFrequencyB}
                          onValueChange={(value) => handleChange("cycleCountFrequencyB", value)}
                          disabled
                        >
                          <SelectTrigger className="bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annually">Annually</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Medium-value items (15% of value)</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">C-Class Items</Label>
                        <Select
                          value={options.cycleCountFrequencyC}
                          onValueChange={(value) => handleChange("cycleCountFrequencyC", value)}
                          disabled
                        >
                          <SelectTrigger className="bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annually">Annually</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Low-value items (5% of value)</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Next scheduled dates will appear here when automatic cycle counting is enabled.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 6: Display & Formatting */}
            <TabsContent value="display" className="space-y-6 mt-6">
              {/* Number Formatting */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Number Formatting</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure how numbers are displayed throughout the application
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity Decimal Places</Label>
                    <Select
                      value={options.quantityDecimals.toString()}
                      onValueChange={(value) => handleChange("quantityDecimals", parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (Whole numbers)</SelectItem>
                        <SelectItem value="2">2 (e.g., 10.25)</SelectItem>
                        <SelectItem value="4">4 (e.g., 10.2500)</SelectItem>
                        <SelectItem value="6">6 (e.g., 10.250000)</SelectItem>
                        <SelectItem value="8">8 (e.g., 10.25000000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Price Decimal Places</Label>
                    <Select
                      value={options.priceDecimals.toString()}
                      onValueChange={(value) => handleChange("priceDecimals", parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (Whole numbers)</SelectItem>
                        <SelectItem value="2">2 (e.g., 19.99)</SelectItem>
                        <SelectItem value="4">4 (e.g., 19.9900)</SelectItem>
                        <SelectItem value="6">6 (e.g., 19.990000)</SelectItem>
                        <SelectItem value="8">8 (e.g., 19.99000000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Percentage Decimal Places</Label>
                    <Select
                      value={options.percentageDecimals.toString()}
                      onValueChange={(value) => handleChange("percentageDecimals", parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 (e.g., 25%)</SelectItem>
                        <SelectItem value="2">2 (e.g., 25.50%)</SelectItem>
                        <SelectItem value="4">4 (e.g., 25.5000%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Thousand Separator</Label>
                    <Select
                      value={options.thousandSeparator}
                      onValueChange={(value) => handleChange("thousandSeparator", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comma">Comma (1,000)</SelectItem>
                        <SelectItem value="space">Space (1 000)</SelectItem>
                        <SelectItem value="none">None (1000)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Decimal Separator</Label>
                    <Select
                      value={options.decimalSeparator}
                      onValueChange={(value) => handleChange("decimalSeparator", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="period">Period (1.00)</SelectItem>
                        <SelectItem value="comma">Comma (1,00)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Date & Time Formatting */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Date & Time Formatting</h3>
                  <p className="text-sm text-muted-foreground">
                    Configure how dates and times are displayed
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date Format</Label>
                    <Select
                      value={options.dateFormat}
                      onValueChange={(value) => handleChange("dateFormat", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</SelectItem>
                        <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</SelectItem>
                        <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Time Format</Label>
                    <Select
                      value={options.timeFormat}
                      onValueChange={(value) => handleChange("timeFormat", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12hour">12-hour (2:30 PM)</SelectItem>
                        <SelectItem value="24hour">24-hour (14:30)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Time Zone</Label>
                    <Select
                      value={options.timeZone}
                      onValueChange={(value) => handleChange("timeZone", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC+00:00">UTC+00:00 (London)</SelectItem>
                        <SelectItem value="UTC+01:00">UTC+01:00 (Paris)</SelectItem>
                        <SelectItem value="UTC+02:00">UTC+02:00 (Cairo)</SelectItem>
                        <SelectItem value="UTC-05:00">UTC-05:00 (New York)</SelectItem>
                        <SelectItem value="UTC-08:00">UTC-08:00 (Los Angeles)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

            </TabsContent>

            {/* Tab 7: Feature Flags */}
            <TabsContent value="features" className="space-y-6 mt-6">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Feature Flags</h3>
                    <p className="text-sm text-muted-foreground">
                      Enable or disable advanced features for your stock management system
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        resetFeatureFlags();
                        toast.success("Feature flags reset to defaults");
                      }}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Defaults
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        enableAllFeatures();
                        toast.success("All features enabled");
                      }}
                    >
                      Enable All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        disableAllFeatures();
                        toast.info("All features disabled");
                      }}
                    >
                      Disable All
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Core Features */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                      Core Features
                    </Badge>
                    <h4 className="text-base font-semibold">Essential Stock Management</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Core features that can be enabled or disabled based on your business needs
                  </p>

                  <div className="space-y-4 pl-4 border-l-2 border-green-200">
                    {/* Warehouse Management */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="warehouse-management" className="font-medium cursor-pointer">
                            Warehouse & Location Management
                          </Label>
                          {featureFlags.enableWarehouseManagement && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Enable multi-warehouse and location tracking. When disabled, all stock uses a single default location.
                        </p>
                        <div className="mt-2 p-3 rounded-md bg-muted/50 text-xs space-y-1">
                          <p className="font-medium">Enable this if you:</p>
                          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                            <li>Have multiple physical locations or warehouses</li>
                            <li>Need to track stock by specific locations within warehouses</li>
                            <li>Want location-level stock takes and transfers</li>
                          </ul>
                          <p className="font-medium mt-2">Disable this if you:</p>
                          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                            <li>Have a single location</li>
                            <li>Don't need location tracking</li>
                            <li>Want a simpler interface</li>
                          </ul>
                        </div>
                      </div>
                      <Switch
                        id="warehouse-management"
                        checked={featureFlags.enableWarehouseManagement}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableWarehouseManagement', checked);
                          toast.success(
                            checked 
                              ? "Warehouse management enabled - You can now manage multiple locations" 
                              : "Warehouse management disabled - All stock will use a single default location"
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Phase 2 Features */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-300">
                      Phase 2
                    </Badge>
                    <h4 className="text-base font-semibold">Advanced Stock Tracking</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Advanced features for detailed inventory tracking and management
                  </p>

                  <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                    {/* Batch/Lot Tracking */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="batch-tracking" className="font-medium cursor-pointer">
                            Batch/Lot Tracking
                          </Label>
                          {featureFlags.enableBatchLotTracking && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Track stock by batch or lot numbers with quantities and expiry dates
                        </p>
                      </div>
                      <Switch
                        id="batch-tracking"
                        checked={featureFlags.enableBatchLotTracking}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableBatchLotTracking', checked);
                          toast.success(checked ? "Batch/Lot tracking enabled" : "Batch/Lot tracking disabled");
                        }}
                      />
                    </div>

                    {/* Serial Number Tracking */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="serial-tracking" className="font-medium cursor-pointer">
                            Serial Number Tracking
                          </Label>
                          {featureFlags.enableSerialNumberTracking && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Track individual items by unique serial numbers
                        </p>
                      </div>
                      <Switch
                        id="serial-tracking"
                        checked={featureFlags.enableSerialNumberTracking}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableSerialNumberTracking', checked);
                          toast.success(checked ? "Serial number tracking enabled" : "Serial number tracking disabled");
                        }}
                      />
                    </div>

                    {/* Expiry Date Tracking */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="expiry-tracking" className="font-medium cursor-pointer">
                            Expiry Date Tracking
                          </Label>
                          {featureFlags.enableExpiryDateTracking && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Track expiry dates for perishable items and batches
                        </p>
                      </div>
                      <Switch
                        id="expiry-tracking"
                        checked={featureFlags.enableExpiryDateTracking}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableExpiryDateTracking', checked);
                          toast.success(checked ? "Expiry date tracking enabled" : "Expiry date tracking disabled");
                        }}
                      />
                    </div>

                    {/* Stock Reservations */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="reservations" className="font-medium cursor-pointer">
                            Stock Reservations
                          </Label>
                          {featureFlags.enableStockReservations && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Reserve stock for orders and reduce available quantity
                        </p>
                      </div>
                      <Switch
                        id="reservations"
                        checked={featureFlags.enableStockReservations}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableStockReservations', checked);
                          toast.success(checked ? "Stock reservations enabled" : "Stock reservations disabled");
                        }}
                      />
                    </div>

                    {/* Location Capacity Limits */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="capacity-limits" className="font-medium cursor-pointer">
                            Location Capacity Limits
                          </Label>
                          {featureFlags.enableLocationCapacityLimits && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Set and enforce maximum capacity limits for storage locations
                        </p>
                      </div>
                      <Switch
                        id="capacity-limits"
                        checked={featureFlags.enableLocationCapacityLimits}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableLocationCapacityLimits', checked);
                          toast.success(checked ? "Location capacity limits enabled" : "Location capacity limits disabled");
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Phase 3 Features */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-300">
                      Phase 3
                    </Badge>
                    <h4 className="text-base font-semibold">Workflow & Automation</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Advanced workflow features for enterprise-level operations
                  </p>

                  <div className="space-y-4 pl-4 border-l-2 border-purple-200">
                    {/* Transfer Approval Workflow */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="approval-workflow" className="font-medium cursor-pointer">
                            Transfer Approval Workflow
                          </Label>
                          {featureFlags.enableTransferApprovalWorkflow && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Require approval for stock transfers above certain thresholds
                        </p>
                      </div>
                      <Switch
                        id="approval-workflow"
                        checked={featureFlags.enableTransferApprovalWorkflow}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableTransferApprovalWorkflow', checked);
                          toast.success(checked ? "Transfer approval workflow enabled" : "Transfer approval workflow disabled");
                        }}
                      />
                    </div>

                    {/* Scheduled Transfers */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="scheduled-transfers" className="font-medium cursor-pointer">
                            Scheduled Transfers
                          </Label>
                          {featureFlags.enableScheduledTransfers && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Schedule stock transfers to execute at a future date/time
                        </p>
                      </div>
                      <Switch
                        id="scheduled-transfers"
                        checked={featureFlags.enableScheduledTransfers}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableScheduledTransfers', checked);
                          toast.success(checked ? "Scheduled transfers enabled" : "Scheduled transfers disabled");
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Reporting Features */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-300">
                      Reporting
                    </Badge>
                    <h4 className="text-base font-semibold">Analytics & Reporting</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Advanced reporting and analytics capabilities
                  </p>

                  <div className="space-y-4 pl-4 border-l-2 border-orange-200">
                    {/* Advanced Reporting */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="advanced-reporting" className="font-medium cursor-pointer">
                            Advanced Reporting
                          </Label>
                          {featureFlags.enableAdvancedReporting && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Enable advanced analytics, custom reports, and data exports
                        </p>
                      </div>
                      <Switch
                        id="advanced-reporting"
                        checked={featureFlags.enableAdvancedReporting}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableAdvancedReporting', checked);
                          toast.success(checked ? "Advanced reporting enabled" : "Advanced reporting disabled");
                        }}
                      />
                    </div>

                    {/* Stock Valuation */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="stock-valuation" className="font-medium cursor-pointer">
                            Stock Valuation Display
                          </Label>
                          {featureFlags.enableStockValuation && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                              ✓ Enabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Show unit cost and total value in stock information displays
                        </p>
                      </div>
                      <Switch
                        id="stock-valuation"
                        checked={featureFlags.enableStockValuation}
                        onCheckedChange={(checked) => {
                          updateFeatureFlag('enableStockValuation', checked);
                          toast.success(checked ? "Stock valuation display enabled" : "Stock valuation display disabled");
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        About Feature Flags
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Feature flags allow you to enable or disable advanced features without code changes. 
                        Your settings are saved automatically and persist across sessions. You can enable features 
                        gradually as you're ready to use them.
                      </p>
                      <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                        <li><strong>Phase 2:</strong> Advanced tracking features (batch, serial, expiry, reservations)</li>
                        <li><strong>Phase 3:</strong> Workflow automation and approval processes</li>
                        <li><strong>Reporting:</strong> Enhanced analytics and reporting capabilities</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Valuation Method Warning Dialog */}
      <ValuationMethodWarningDialog
        open={isWarningDialogOpen}
        onOpenChange={setIsWarningDialogOpen}
        onConfirm={confirmValuationMethodChange}
        currentMethod={options.valuationMethod}
        newMethod={pendingValuationMethod || options.valuationMethod}
      />

      {/* Stock Take & Cycle Count Help Dialog */}
      <Dialog open={stockTakeHelpDialog} onOpenChange={setStockTakeHelpDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Stock Take & Cycle Count Settings Help</DialogTitle>
            <DialogDescription>
              Understanding how stock take settings apply to different stock take types
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Overview */}
            <div>
              <h3 className="font-semibold mb-2">Stock Take Types Overview</h3>
              <p className="text-sm text-muted-foreground mb-3">
                The system supports four types of stock takes, each designed for different counting scenarios:
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">Full</Badge>
                  <div className="flex-1">
                    <p className="text-sm"><strong>Full Stock Take:</strong> Count all stock items at once (typically annual or semi-annual)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">Partial</Badge>
                  <div className="flex-1">
                    <p className="text-sm"><strong>Partial Stock Take:</strong> Count a manually selected subset of items</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">Location</Badge>
                  <div className="flex-1">
                    <p className="text-sm"><strong>Location Audit:</strong> Count all items in a specific location or warehouse</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">Cycle</Badge>
                  <div className="flex-1">
                    <p className="text-sm"><strong>Cycle Count:</strong> Automated, ABC-based continuous counting (most efficient)</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* General Settings */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
                <h3 className="font-semibold">General Settings (All Types)</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                These settings apply to <strong>all four stock take types</strong>:
              </p>
              <div className="bg-muted rounded-lg p-3 space-y-2">
                <div>
                  <p className="text-sm font-medium">Count Method</p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 mt-1">
                    <li><strong>Blind Count:</strong> Hides system quantities from counter (more accurate, prevents bias)</li>
                    <li><strong>Guided Count:</strong> Shows system quantities to counter (faster, but may introduce bias)</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium">Variance Tolerance</p>
                  <p className="text-sm text-muted-foreground ml-2">Acceptable variance percentage before requiring approval (e.g., 2% = ±2 units on 100 qty)</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Auto-adjust within tolerance</p>
                  <p className="text-sm text-muted-foreground ml-2">Automatically adjust inventory for variances within tolerance (no approval needed)</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Require approval for variances above tolerance</p>
                  <p className="text-sm text-muted-foreground ml-2">Variances exceeding tolerance must be approved before adjusting inventory</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Cycle Count Specific */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full bg-purple-500" />
                <h3 className="font-semibold">Cycle Count Frequency (Cycle Count Only)</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                These settings apply <strong>only to Cycle Count</strong> stock takes and work with ABC Classification:
              </p>
              <div className="bg-muted rounded-lg p-3 space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-red-500">A</Badge>
                    <p className="text-sm font-medium">A-Class Items (High Value)</p>
                  </div>
                  <p className="text-sm text-muted-foreground ml-2">
                    Typically 20% of items representing 80% of inventory value. Recommended: <strong>Monthly</strong> counting
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-yellow-500">B</Badge>
                    <p className="text-sm font-medium">B-Class Items (Medium Value)</p>
                  </div>
                  <p className="text-sm text-muted-foreground ml-2">
                    Typically 30% of items representing 15% of inventory value. Recommended: <strong>Quarterly</strong> counting
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-green-500">C</Badge>
                    <p className="text-sm font-medium">C-Class Items (Low Value)</p>
                  </div>
                  <p className="text-sm text-muted-foreground ml-2">
                    Typically 50% of items representing 5% of inventory value. Recommended: <strong>Annually</strong> counting
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* How It Works */}
            <div>
              <h3 className="font-semibold mb-2">How Cycle Count Works with ABC Classification</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">ABC Classification Assigns Classes</p>
                    <p className="text-sm text-muted-foreground">Items are classified as A, B, or C based on their annual usage value (quantity × cost)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Frequencies Determine Schedule</p>
                    <p className="text-sm text-muted-foreground">Each item gets a "Next Cycle Count Date" based on its class frequency (A: monthly, B: quarterly, C: annually)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Items Become Due for Counting</p>
                    <p className="text-sm text-muted-foreground">When today's date ≥ Next Cycle Count Date, items appear in the "Due for Counting" list</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">4</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Create Cycle Count Stock Take</p>
                    <p className="text-sm text-muted-foreground">Select "Cycle Count" type when creating a stock take, and due items are automatically included</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">5</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Count and Update</p>
                    <p className="text-sm text-muted-foreground">After counting, the system updates "Last Cycle Count Date" and calculates the next due date</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">6</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Cycle Repeats Automatically</p>
                    <p className="text-sm text-muted-foreground">The process continues automatically, ensuring continuous inventory accuracy</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Example */}
            <div>
              <h3 className="font-semibold mb-2">Example: 1,000 Items</h3>
              <div className="bg-muted rounded-lg p-3">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-red-600">A-Class (200 items)</p>
                    <p className="text-muted-foreground">$8M value (80%)</p>
                    <p className="text-muted-foreground">Monthly = 12×/year</p>
                    <p className="font-medium">2,400 counts/year</p>
                  </div>
                  <div>
                    <p className="font-medium text-yellow-600">B-Class (300 items)</p>
                    <p className="text-muted-foreground">$1.5M value (15%)</p>
                    <p className="text-muted-foreground">Quarterly = 4×/year</p>
                    <p className="font-medium">1,200 counts/year</p>
                  </div>
                  <div>
                    <p className="font-medium text-green-600">C-Class (500 items)</p>
                    <p className="text-muted-foreground">$500K value (5%)</p>
                    <p className="text-muted-foreground">Annually = 1×/year</p>
                    <p className="font-medium">500 counts/year</p>
                  </div>
                </div>
                <Separator className="my-3" />
                <div className="text-center">
                  <p className="text-sm font-medium">Total: 4,100 counts/year</p>
                  <p className="text-sm text-muted-foreground">(vs 12,000 if counting all items monthly)</p>
                  <p className="text-sm font-semibold text-green-600 mt-1">66% reduction in counting effort!</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Configuration Link */}
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Configure ABC Classification</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    To set up ABC classifications and thresholds, go to <strong>AI Forecasting & Analytics → ABC Classification → Configuration Tab</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setStockTakeHelpDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
